(() => {
  const TRADE_IN_EVALUATION_PROP = "_tradeIn_evaluationId";
  const TRADE_IN_EXPIRES_AT_PROP = "_tradeIn_expiresAt";
  const TRADE_IN_EXPIRED_PROP = "_tradeIn_expired";
  const TRADE_IN_TYPE_PROP = "_tradeIn_type";

  if (window["__flipCheckoutSubmitServiceInitialized"]) {
    return;
  }
  window["__flipCheckoutSubmitServiceInitialized"] = true;

  const ensureCheckoutEventName = () => {
    if (typeof window.PUB_SUB_EVENTS === "object" && window.PUB_SUB_EVENTS) {
      if (!window.PUB_SUB_EVENTS.flipCheckoutSubmit) {
        window.PUB_SUB_EVENTS.flipCheckoutSubmit = "flip-checkout-submit";
      }
      return window.PUB_SUB_EVENTS.flipCheckoutSubmit;
    }

    return "flip-checkout-submit";
  };

  const isCheckoutPath = (value) => /checkouts?(?:\/|\?|$)/.test(value || "");

  const resolveCheckoutUrl = (rawUrl) => {
    if (!rawUrl) {
      return "/checkout";
    }

    const parser = document.createElement("a");
    parser.href = rawUrl;

    const normalizedPath = (parser.pathname || "") + (parser.search || "");
    if (isCheckoutPath(normalizedPath)) {
      return (parser.pathname || "") + (parser.search || "") + (parser.hash || "");
    }

    if (isCheckoutPath(rawUrl)) {
      return rawUrl;
    }

    return "/checkout";
  };

  const getPublishFn = () => {
    if (typeof window.publish === "function") {
      return window.publish;
    }

    if (typeof publish === "function") {
      return publish;
    }

    return null;
  };

  const hasTradeInCheckoutHandler = () =>
    Boolean(window.flipCheckoutSubmit && window.flipCheckoutSubmit.hasTradeInCheckoutHandler === true);

  const publishCheckoutSubmit = (checkoutUrl) => {
    const publishFn = getPublishFn();
    if (!publishFn || !hasTradeInCheckoutHandler()) {
      return false;
    }

    publishFn(ensureCheckoutEventName(), {
      checkoutUrl: resolveCheckoutUrl(checkoutUrl),
    });

    return true;
  };

  const asNonEmptyString = (value) => {
    if (typeof value !== "string") {
      return null;
    }

    const normalized = value.trim();
    return normalized || null;
  };

  const extractDatePart = (value) => {
    const normalized = asNonEmptyString(value);
    if (!normalized) {
      return null;
    }

    const match = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  const isTradeInExpired = (properties) => {
    const evaluationId = asNonEmptyString(properties && properties[TRADE_IN_EVALUATION_PROP]);
    const expiresAt = asNonEmptyString(properties && properties[TRADE_IN_EXPIRES_AT_PROP]);

    if (!evaluationId || evaluationId.toLowerCase() === "null" || !expiresAt) {
      return false;
    }

    const currentTimestampMs = Date.now();
    const expiresAtTimestampMs = Date.parse(expiresAt);

    if (Number.isFinite(expiresAtTimestampMs) && Number.isFinite(currentTimestampMs)) {
      return currentTimestampMs >= expiresAtTimestampMs;
    }

    const expiresAtDate = extractDatePart(expiresAt);
    if (!expiresAtDate) {
      return false;
    }

    const currentDate = new Date(currentTimestampMs).toISOString().slice(0, 10);
    return currentDate >= expiresAtDate;
  };

  const normalizeTradeInType = (value) => {
    const normalized = asNonEmptyString(value);
    if (normalized === 'FLIP' || normalized === 'RADERA') {
      return normalized;
    }

    return "FLIP";
  };

  const handleJsonResponse = async (response) => {
    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch (error) {
        errorBody = {
          status: response.status,
          statusText: response.statusText,
        };
      }
      throw errorBody;
    }

    return response.json();
  };

  const fetchCart = async () => {
    const response = await fetch("/cart.js", {
      headers: {
        Accept: "application/json",
      },
    });

    return handleJsonResponse(response);
  };

  const updateLineByKey = async (lineItemKey, payload) => {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: lineItemKey,
        ...payload,
      }),
    });

    return handleJsonResponse(response);
  };

  const cleanupExpiredTradeInLines = async () => {
    let cart = await fetchCart();
    const refreshedLines = Array.isArray(cart && cart.items) ? cart.items : [];
    const expiredLines = refreshedLines.filter((line) => isTradeInExpired(line && line.properties));

    for (const line of expiredLines) {
      const currentProps = { ...((line && line.properties) || {}) };
      const nextProps = { ...currentProps };
      const expiresAt = asNonEmptyString(currentProps[TRADE_IN_EXPIRES_AT_PROP]);

      nextProps[TRADE_IN_EVALUATION_PROP] = "null";
      nextProps[TRADE_IN_EXPIRES_AT_PROP] = expiresAt || "null";
      nextProps[TRADE_IN_EXPIRED_PROP] = "true";
      nextProps[TRADE_IN_TYPE_PROP] = normalizeTradeInType(currentProps[TRADE_IN_TYPE_PROP]);

      try {
        cart = await updateLineByKey(line.key, {
          quantity: line.quantity,
          properties: nextProps,
        });
      } catch (error) {
        console.error("Failed to update expired trade-in properties:", line && line.key, error);
      }
    }

    return cart;
  };

  const continueToCheckout = async (checkoutUrl) => {
    const resolvedCheckoutUrl = resolveCheckoutUrl(checkoutUrl);

    if (publishCheckoutSubmit(resolvedCheckoutUrl)) {
      return;
    }

    const serviceState = (window.flipCheckoutSubmit = window.flipCheckoutSubmit || {});
    if (serviceState.inFlight === true) {
      return;
    }

    serviceState.inFlight = true;
    try {
      await cleanupExpiredTradeInLines();
    } catch (error) {
      console.error("Trade-In theme fallback cleanup failed before redirect:", error);
    } finally {
      serviceState.inFlight = false;
      window.location.assign(resolvedCheckoutUrl);
    }
  };

  const isCheckoutSubmitter = (submitter) => submitter instanceof HTMLElement && submitter.matches("[name='checkout']");

  const bindCheckoutForm = (form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (form.getAttribute("data-flip-checkout-submit-bound") === "true") {
      return;
    }
    form.setAttribute("data-flip-checkout-submit-bound", "true");

    form.addEventListener("submit", (event) => {
      const submitEvent = event;
      const submitter = submitEvent.submitter instanceof HTMLElement ? submitEvent.submitter : document.activeElement;

      if (!isCheckoutSubmitter(submitter)) {
        return;
      }

      const submitterAction =
        submitter instanceof HTMLElement ? submitter.getAttribute("formaction") || submitter.getAttribute("data-checkout-url") : null;
      const formAction = form.getAttribute("action") || form.action;
      event.preventDefault();
      void continueToCheckout(submitterAction || formAction);
    });
  };

  const bindKnownCheckoutForms = () => {
    bindCheckoutForm(document.querySelector("#cart"));
    bindCheckoutForm(document.querySelector("#CartDrawer-Form"));
    bindCheckoutForm(document.querySelector("#CTOCartDrawer-Form"));
    bindCheckoutForm(document.querySelector("#cart-notification-form"));
  };

  window.flipCheckoutSubmit = window.flipCheckoutSubmit || {};
  window.flipCheckoutSubmit.publishCheckoutSubmit = publishCheckoutSubmit;
  window.flipCheckoutSubmit.continueToCheckout = continueToCheckout;
  window.flipCheckoutSubmit.bindKnownCheckoutForms = bindKnownCheckoutForms;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindKnownCheckoutForms);
  } else {
    bindKnownCheckoutForms();
  }
})();
